import { getSidebarItem } from "../../../config/sidebarConfig";
import { circleHref } from "../../../routes";
import { circleUsersQuery, usersQuery } from "../../../services/queries";
import { viewQuery } from "../scopedQuery";
import { toCircle } from "../../circles/circleHandover";
import { askedHow } from "./askedHow";
import { openPageEntry } from "./openPage";
import { askForCircle, loadCircles, pickCircle, resolveCircle } from "./pickCircle";
import { pickUser } from "./pickUser";
import { AnswerContext, defineEntry, HandoffEntry, isSlotQuestion } from "./types";
import { countOf, isAre, listOf, listUpTo } from "./wording";

const circlesPage = getSidebarItem("circles");
const openCircles = () => ({ label: `Open ${circlesPage.label}`, href: circlesPage.path });

const members = (n: number) => countOf(n, "member");

const NO_CIRCLES = "You don't have any Circles yet.";

/** "How many circles do we have?" is the number of Circles, not how many people are in each one. */
const ASKS_FOR_CIRCLE_COUNT = /how many circles|what circles|list our circles/i;
const circleList = (sizes: string[], names: string[], asked: string, failed = false) => {
  if (names.length === 0) return NO_CIRCLES;
  const list = `You have ${countOf(names.length, "Circle")}: ${listOf(names)}.`;
  if (ASKS_FOR_CIRCLE_COUNT.test(asked)) return list;
  // Asked how big they are, and the members didn't load: said so, never a count of zero
  if (failed) return `I couldn't load the member counts right now. ${list}`;
  if (sizes.length === 0) return list;
  return `${listOf(sizes)}.`;
};

type Circle = Awaited<ReturnType<typeof loadCircles>>[number];

/** A Circle's members, as its page lists and counts them. */
const loadMembers = ({ user, token, queryClient }: AnswerContext, circleId: string) =>
  queryClient.fetchQuery(viewQuery(user, circleUsersQuery(circleId, user?.schoolId, token)));

/** Every Circle this person manages, with its members. One request per Circle, as each Circle's page makes. */
const loadCirclesWithMembers = async (ctx: AnswerContext) => {
  const circles = await loadCircles(ctx);
  return Promise.all(circles.map(async (circle) => ({ circle, members: await loadMembers(ctx, circle.circleId) })));
};

/** The school's Users, as a Circle's Add users list offers them (by schoolId, so a picked User is always there). */
const loadUsers = (ctx: AnswerContext) =>
  ctx.queryClient.fetchQuery(viewQuery(ctx.user, usersQuery(ctx.user?.schoolId, ctx.token)));

/** "Grade 2 has 2 members" */
const sizeOf = ({ circle, members: list }: { circle: Circle; members: unknown[] }) =>
  `${circle.name} has ${members(list.length)}`;

/** "Add Jane to Youth": the Circle's page, with Jane picked to add. Nothing is saved until Add user. */
const addUserEntry: HandoffEntry = {
  id: "circles.addUser",
  sidebarItem: "circles",
  examples: [
    "Add someone to a circle",
    "Add a member to a circle",
    "Add someone to a class",
    "Add someone to a group",
    "Add a person to a circle",
    "Add an existing user to a circle",
    "Put someone in a circle",
    "Put someone in a class",
    "Add to a circle",
    "Give someone access to a circle",
    "Add a user to a circle",
  ],
  handoff: async (said, ctx) => {
    const circles = await loadCircles(ctx);
    if (circles.length === 0) return { kind: "cannot", text: NO_CIRCLES };
    const names = circles.map((c) => c.name);
    const resolved = said.audience ? resolveCircle(circles, said.audience) : { reason: "missing" as const };
    const picked = askForCircle(resolved, "Which Circle should they join?", names);
    if (isSlotQuestion(picked)) return picked;
    const circle = picked.circle;
    const [users, current] = await Promise.all([loadUsers(ctx), loadMembers(ctx, circle.circleId)]);
    const inCircle = new Set(current.map((m) => m.userId));
    const question = `Who would you like to add to ${circle.name}?`;
    const pick = pickUser(users, said.user ?? "", question, users.filter((u) => !inCircle.has(u.id)));
    if (isSlotQuestion(pick)) return pick;
    if (inCircle.has(pick.user.id)) {
      return { kind: "cannot", text: `${pick.user.name} is already in ${circle.name}.` };
    }
    return {
      kind: "ready",
      text: "Here's what I understood. Open it to check. Nothing is saved until you press Add user.",
      summary: [
        { label: "Add", value: pick.user.name },
        { label: "To", value: circle.name },
      ],
      to: toCircle(circle.circleId, { addUser: pick.user.id }),
    };
  },
};

/** ": Jo and Sipho", or nothing when no member has a name yet (counted all the same). */
const namedList = (names: string[]) => (names.length > 0 ? `: ${listUpTo(names)}` : "");

export const circlesEntries = [
  openPageEntry("circles", "It's where you manage your Circles and who is in each one.", [
    "Where do I manage our circles?",
    "I can't find the Circles button",
    "Where is the Circles page?",
    "How do I open Circles?",
    "Take me to circles",
    "Where can I see who is in a circle?",
    "Show me my circles",
  ]),

  // A Circle's members, as its page lists them
  defineEntry<{ circle: Circle | null; count: number; names: string[] }>({
    id: "circles.members",
    sidebarItem: "circles",
    examples: [
      "Who's in Grade 3?",
      "Who is in this circle?",
      "Who's in the class?",
      "Who are the members of the circle?",
      "List the circle's members",
      "Show me the members of a circle",
      "How many people are in the circle?",
      "How many members does the circle have?",
      "Who belongs to the circle?",
      "Members of the class",
      "How big is the circle?",
      ...askedHow("see who is in a circle"),
    ],
    slots: { circle: "" },
    answer: {
      load: async ({ circle: said = "" }, ctx) => {
        const circles = await loadCircles(ctx);
        if (circles.length === 0) return { circle: null, count: 0, names: [] };
        const pick = pickCircle(circles, said);
        if (isSlotQuestion(pick)) return pick;
        const list = await loadMembers(ctx, pick.circle.circleId);
        // Counted as the page counts them, even a member with no name yet
        return { circle: pick.circle, count: list.length, names: list.map((m) => m.name).filter(Boolean) };
      },
      templates: [
        ({ circle, count, names }) =>
          !circle
            ? NO_CIRCLES
            : count === 0
              ? `${circle.name} has no members yet.`
              : `${circle.name} has ${members(count)}${namedList(names)}.`,
        ({ circle, count, names }) =>
          !circle
            ? NO_CIRCLES
            : count === 0
              ? `There's no one in ${circle.name} yet: it has no members.`
              : `There ${isAre(count)} ${members(count)} in ${circle.name}${namedList(names)}.`,
        ({ circle, count, names }) =>
          !circle
            ? NO_CIRCLES
            : count === 0
              ? `${circle.name} has no members so far.`
              : names.length === 0
                ? `${circle.name} has ${members(count)}, none named yet.`
                : `In ${circle.name} (${members(count)}): ${listUpTo(names)}.`,
      ],
      link: (_, { circle }) =>
        circle ? { label: `Open ${circle.name}`, href: circleHref(circle.circleId) } : openCircles(),
      subjectOf: ({ circle }) => circle?.name,
    },
  }),

  // Which of the Circles this person manages a User is a member of
  defineEntry<{ name: string; circles: string[] } | { name: null; circles: [] }>({
    id: "circles.ofUser",
    sidebarItem: "circles",
    examples: [
      "Which circles is this parent in?",
      "Which Circles is Jane Dube in?",
      "What circles is the user in?",
      "Which classes is the parent in?",
      "What groups does the user belong to?",
      "Is the parent in a circle?",
      "Which circles does the user belong to?",
      "Show me the user's circles",
      "Circles for a user",
      "What circle is the parent part of?",
      "Find which circles a user is in",
    ],
    slots: { user: "" },
    answer: {
      load: async ({ user: said = "" }, ctx) => {
        const [users, withMembers] = await Promise.all([loadUsers(ctx), loadCirclesWithMembers(ctx)]);
        if (withMembers.length === 0) return { name: null, circles: [] };
        const pick = pickUser(users, said, "Which User would you like to know about?");
        if (isSlotQuestion(pick)) return pick;
        return {
          name: pick.user.name,
          circles: withMembers
            .filter(({ members: list }) => list.some((m) => m.userId === pick.user.id))
            .map(({ circle }) => circle.name),
        };
      },
      templates: [
        ({ name, circles }) =>
          name === null
            ? NO_CIRCLES
            : circles.length === 0
              ? `${name} isn't in any of your Circles yet.`
              : `${name} is in ${listOf(circles)}.`,
        ({ name, circles }) =>
          name === null
            ? NO_CIRCLES
            : circles.length === 0
              ? `${name} isn't a member of any of your Circles.`
              : `${name} is a member of ${listOf(circles)}.`,
        ({ name, circles }) =>
          name === null
            ? NO_CIRCLES
            : circles.length === 0
              ? `None of your Circles have ${name} in them yet.`
              : `You'll find ${name} in ${listOf(circles)}.`,
      ],
      link: () => openCircles(),
    },
  }),

  // Every Circle's size, as each Circle's page counts its members
  defineEntry<{ sizes: string[]; names: string[]; failed?: boolean }>({
    id: "circles.sizes",
    sidebarItem: "circles",
    examples: [
      "How many members does each circle have?",
      "How big are our circles?",
      "Circle sizes",
      "How many people are in each circle?",
      "Member count per circle",
      "How many users in every circle?",
      "Show me the size of each circle",
      "Number of members in our circles",
      "What size is each class?",
      "Which circle has the most members?",
      "How many circles do we have?",
      "What circles do we have?",
      "List our circles",
    ],
    answer: {
      load: async (_, ctx) => {
        const names = (await loadCircles(ctx)).map((circle) => circle.name);
        try {
          return { sizes: (await loadCirclesWithMembers(ctx)).map(sizeOf), names };
        } catch {
          return { sizes: [] as string[], names, failed: true };
        }
      },
      templates: [
        ({ sizes, names, failed }, _, asked = "") => circleList(sizes, names, asked, failed),
        ({ sizes, names, failed }, _, asked = "") => circleList(sizes, names, asked, failed),
        ({ sizes, names, failed }, _, asked = "") => circleList(sizes, names, asked, failed),
      ],
      link: () => openCircles(),
    },
  }),

  // The Circles whose page shows no members
  defineEntry<{ hasCircles: boolean; empty: string[] }>({
    id: "circles.empty",
    sidebarItem: "circles",
    examples: [
      "Which circles have no parents in them?",
      "Which circles are empty?",
      "Are any circles empty?",
      "Circles with no members",
      "Which circles have nobody in them?",
      "Which classes have no parents?",
      "Show me the empty circles",
      "Which circles have no users?",
      "Do any circles have no members?",
      "Empty circles",
    ],
    answer: {
      load: async (_, ctx) => {
        const withMembers = await loadCirclesWithMembers(ctx);
        return {
          hasCircles: withMembers.length > 0,
          empty: withMembers.filter(({ members: list }) => list.length === 0).map(({ circle }) => circle.name),
        };
      },
      templates: [
        ({ hasCircles, empty }) =>
          !hasCircles
            ? NO_CIRCLES
            : empty.length === 0
              ? "Every Circle has members."
              : `${listOf(empty)} ${empty.length === 1 ? "has" : "have"} no members yet.`,
        ({ hasCircles, empty }) =>
          !hasCircles
            ? NO_CIRCLES
            : empty.length === 0
              ? "None of your Circles are empty."
              : `${countOf(empty.length, "Circle")} ${isAre(empty.length)} empty: ${listOf(empty)}.`,
        ({ hasCircles, empty }) =>
          !hasCircles
            ? NO_CIRCLES
            : empty.length === 0
              ? "All your Circles have members in them."
              : `No one is in ${listOf(empty)} yet.`,
      ],
      link: () => openCircles(),
    },
  }),

  addUserEntry,
];
